import TextInput from "components/Inputs/TextInput";
import SelectField from "components/Select/Select";
import Container from "components/ui/Container";
import { Controller, useFormContext } from "react-hook-form";
import resources from "resources.json";
import { useGetAccountGroups } from "./useGetAccountGroups";
import FormContainer from "components/ui/FormContainer";
import Spinner from "components/ui/Spinner";
import { Divider } from "antd";
import { useGetAccountGroupBasicDetails } from "./useGetAccountGroupBasicDetails";
import { FormValues } from ".";
import SingleSelect from "components/Select/SingleSelect";
import InputFieldGroupContainer from "components/ui/InputFieldsGroupContainer";
import ButtonsContainer from "components/ui/ButtonsContainer";
import Button from "components/ui/Button";
import Footer from "components/ui/Footer";
import { useSaveNewAccountGroupDetails } from "./useSaveNewAccountGroupDetails";
import AppLoader from "components/ui/AppLoader";
import { useEffect, useMemo, useRef, useState } from "react";
import { Modal } from "components/Modal";
import toast from "react-hot-toast";
import CloseButton from "components/CloseButton";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";

type BasicDetailsProps = {
  accountNumber: string;
  handleTabChange: (tab: string) => void
};

export default function BasicDetails({
  accountNumber,
  handleTabChange
}: BasicDetailsProps) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [openNoOtherDetailsAvailableModal, setOpenNoOtherDetailsAvailableModal] = useState(false);
  const isAccountGroupChangeSaved = useRef(false);

  const accountGroupsData = queryClient.getQueryData([
    "changeAccountGroupsInitData",
    accountNumber,
  ]) as any;

  const {
    control,
    formState: { errors },
    watch,
    handleSubmit,
  } = useFormContext<FormValues>();

  const newAccountGroupId = watch("newAccountGroupId");
  const {
    mutate: saveNewAccountGroupDetails,
    isLoading: isSaveNewAccountGroupDetailsLoading,
  } = useSaveNewAccountGroupDetails();

  const {
    isFetching: isAccountGroupBasicDetailsLoading,
    data: newAccountGroupBasicDetailsData,
  } = useGetAccountGroupBasicDetails({
    newAccountGroupId: accountGroupsData?.isAccountGroupChangeAlreadyRequested ? null : newAccountGroupId,
    accountNumber,
  });

  useEffect(() => {
    if (!isAccountGroupChangeSaved.current || !accountGroupsData) {
      return;
    }

    if (!accountGroupsData?.isAccountGroupChangeAlreadyRequested) {
      setOpenNoOtherDetailsAvailableModal(true);
    }
  }, [isAccountGroupChangeSaved.current, accountGroupsData]);

  const onSubmit = handleSubmit((data) => {
    saveNewAccountGroupDetails(
      {
        ...data,
        accountNumber,
      },
      {
        onSuccess: (response) => {
          if (response.status === 200) {
            isAccountGroupChangeSaved.current = true;
          }
        },
      },
    );
  });

  const handleCloseModal = () => {
    setOpenNoOtherDetailsAvailableModal(false);

    if (isAccountGroupChangeSaved.current) {
      if (!accountGroupsData?.isAccountGroupChangeAlreadyRequested) {
        navigate(-1);
      } else {
        handleTabChange?.("OTHER_ACTION")
      }
    }
  }

  const getNoOtherDetailsAvailableTitle = () => {
    if (isAccountGroupChangeSaved.current) {
      if (!accountGroupsData?.isAccountGroupChangeAlreadyRequested) {
        return resources.modal.body.ACCOUNT_GROUP_CHANGE_SUCCESS_WITHOUT_PENDING_ACTION;
      }
      return resources.modal.body.ACCOUNT_GROUP_CHANGE_SUCCESS_WITH_PENDING_ACTION;
    }
  };

  return (
    <>
      <Modal
        open={openNoOtherDetailsAvailableModal}
        titleContent={<p>Info</p>}
        content={<p className="bold__text">{getNoOtherDetailsAvailableTitle()}</p>}
        closeFn={handleCloseModal}
        closeIcon={<CloseButton onClose={handleCloseModal} />}
        escFn={handleCloseModal}
        actions={<ButtonsContainer>
          <Button buttonType="primary" onClick={handleCloseModal} type="button">Close</Button>
        </ButtonsContainer>}
      />
      {isSaveNewAccountGroupDetailsLoading && <AppLoader />}
      <FormContainer hasFooter={!accountGroupsData?.isAccountGroupChangeAlreadyRequested}>
        <form id="basic-details-form">
          <Container className="col col-3">
            <TextInput
              value={accountGroupsData?.oldAccountGroupName}
              disabled
              label={resources.label.CURRENT_ACCOUNT_GROUP_LABEL}
              required
            />
            <Controller
              name="newAccountGroupId"
              control={control}
              render={({ field }) => (
                <SingleSelect
                  {...field}
                  options={accountGroupsData?.accountGroupList}
                  value={
                    accountGroupsData?.accountGroupList?.find(
                      (opt: any) => opt.value === field.value,
                    ) || null
                  }
                  onChange={(option: any) => {
                    field.onChange(option?.value || "");
                  }}
                  error={errors?.newAccountGroupId?.message as string}
                  placeholder="Select Account Group"
                  label={resources.label.NEW_ACCOUNT_GROUP_LABEL}
                  required
                  isDisabled={
                    accountGroupsData?.isAccountGroupChangeAlreadyRequested
                  }
                  content={resources.tooltipMessage.NEW_ACCOUNT_GROUP_INFO}
                />
              )}
            />
          </Container>
          <Divider className="line" />
          {isAccountGroupBasicDetailsLoading ? (
            <Spinner />
          ) : (
            newAccountGroupBasicDetailsData && (
              <InputFieldGroupContainer
                className="col col-3"
                label={resources.label.DETAIL_FOR_NEW_ACCOUNT_GROUP_LABEL}
              >
                <TextInput
                  disabled
                  label={resources.label.ACCOUNT_TYPE_LABEL}
                  value={newAccountGroupBasicDetailsData?.accountGroupType}
                />
                <TextInput
                  disabled
                  label={resources.label.BILLING_CYCLE_LABEL}
                  value={newAccountGroupBasicDetailsData?.billingCycle}
                />
                <TextInput
                  disabled
                  label={resources.label.JURISDICTION_LABEL}
                  value={newAccountGroupBasicDetailsData?.jurisdiction}
                />
                <TextInput
                  disabled
                  label={resources.label.MOB_E_BILLING_INSTALLATION_ID_LABEL}
                  value={newAccountGroupBasicDetailsData?.mobInstallationId}
                />
                <TextInput
                  disabled
                  label={
                    resources.label.MOB_CUSTOM_REPORTS_INSTALLATION_LABEL
                  }
                  value={
                    newAccountGroupBasicDetailsData?.compliancePortalInstallationId
                  }
                />
              </InputFieldGroupContainer>
            )
          )}
        </form>
      </FormContainer>
      {!accountGroupsData?.isAccountGroupChangeAlreadyRequested && <Footer>
        <ButtonsContainer>
          <Button buttonType="secondary" type="button">
            Cancel
          </Button>
          <Button
            buttonType="primary"
            type="button"
            form="basic-details-form"
            onClick={onSubmit}
          >
            Save & Next
          </Button>
        </ButtonsContainer>
      </Footer>}
    </>
  );
}
